import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import type { BillingAccount, BusinessListFilters, PlatformBusiness, PlatformOverview, PlatformTicket, Registration } from './types';

const admin = (token: string, path: string, init?: RequestInit) =>
  apiFetch(`${API_URL}/admin${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init?.headers || {}) },
  });

export async function fetchOverview(token: string): Promise<PlatformOverview> {
  const res = await admin(token, '/overview');
  if (!res.ok) throw new Error('Failed to load overview');
  return res.json();
}

export async function fetchRegistrations(token: string, status?: string): Promise<Registration[]> {
  const q = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const res = await admin(token, `/registrations${q}`);
  if (!res.ok) throw new Error('Failed to load applications');
  const data = await res.json();
  return (data.registrations || []).sort(
    (a: Registration, b: Registration) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export async function registrationAction(
  token: string,
  id: string,
  action: 'activate' | 'extend' | 'cancel' | 'reject',
  body?: Record<string, unknown>
): Promise<{ emailSent?: boolean; emailError?: string | null }> {
  const path =
    action === 'reject'
      ? `/registrations/${id}/reject`
      : `/registrations/${id}/${action}`;
  const res = await admin(token, path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Failed to ${action}`);
  return data;
}

export async function approveApplication(token: string, id: string, days = 14) {
  const res = await admin(token, `/registrations/${id}/approve-application`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to approve application');
  }
}

export async function sendPaymentLink(token: string, id: string) {
  const res = await admin(token, `/registrations/${id}/send-payment-link`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to send payment link');
  return res.json();
}

export async function fetchBusinesses(token: string, filters?: BusinessListFilters): Promise<PlatformBusiness[]> {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters?.suite && filters.suite !== 'all') params.set('suite', filters.suite);
  if (filters?.filter && filters.filter !== 'all') params.set('filter', filters.filter);
  const q = params.toString() ? `?${params}` : '';
  const res = await admin(token, `/businesses${q}`);
  if (!res.ok) throw new Error('Failed to load businesses');
  const data = await res.json();
  return data.businesses || [];
}

export async function fetchBusiness(token: string, id: number): Promise<PlatformBusiness> {
  const res = await admin(token, `/businesses/${id}`);
  if (!res.ok) throw new Error('Business not found');
  const data = await res.json();
  return data.business;
}

export async function updateBusinessPlan(token: string, id: number, plan: string) {
  const res = await admin(token, `/businesses/${id}/plan`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) throw new Error('Failed to update plan');
}

export async function extendBusinessTrial(token: string, id: number, days = 7) {
  const res = await admin(token, `/businesses/${id}/extend-trial`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days }),
  });
  if (!res.ok) throw new Error('Failed to extend trial');
}

export async function fetchBillingAccounts(token: string): Promise<BillingAccount[]> {
  const res = await admin(token, '/billing');
  if (!res.ok) throw new Error('Failed to load billing');
  const data = await res.json();
  return data.accounts || [];
}

export async function toggleBusiness(token: string, id: number) {
  const res = await admin(token, `/businesses/${id}/toggle`, { method: 'PUT' });
  if (!res.ok) throw new Error('Failed to update account');
}

export async function fetchPlatformTickets(
  token: string,
  opts?: { business_id?: number; category?: string; status?: string }
): Promise<PlatformTicket[]> {
  const params = new URLSearchParams();
  if (opts?.business_id) params.set('business_id', String(opts.business_id));
  if (opts?.category) params.set('category', opts.category);
  if (opts?.status) params.set('status', opts.status);
  const q = params.toString() ? `?${params}` : '';
  const res = await admin(token, `/support/tickets${q}`);
  if (!res.ok) throw new Error('Failed to load tickets');
  const data = await res.json();
  return data.tickets || [];
}

export async function fetchPlatformTicket(token: string, id: string): Promise<PlatformTicket> {
  const res = await admin(token, `/support/tickets/${id}`);
  if (!res.ok) throw new Error('Ticket not found');
  const data = await res.json();
  return data.ticket;
}

export async function createPlatformTicket(
  token: string,
  payload: { business_id: number; subject: string; message: string; priority?: string; category?: string }
) {
  const res = await admin(token, '/support/tickets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to create ticket');
  return res.json();
}

export async function replyPlatformTicket(token: string, id: string, message: string) {
  const res = await admin(token, `/support/tickets/${id}/responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  if (!res.ok) throw new Error('Failed to send reply');
}

export async function updateTicketStatus(token: string, id: string, status: string) {
  const res = await admin(token, `/support/tickets/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update status');
}

export async function fetchAdminLogs(token: string, limit = 8) {
  const res = await admin(token, `/logs?limit=${limit}`);
  if (!res.ok) throw new Error('Failed to load admin logs');
  const data = await res.json();
  return data.logs || [];
}

export async function fetchGdprOverview(token: string) {
  const res = await admin(token, '/gdpr/overview');
  if (!res.ok) throw new Error('Failed to load GDPR overview');
  return res.json();
}

export async function fetchErasureQueue(token: string) {
  const res = await admin(token, '/gdpr/erasure');
  if (!res.ok) throw new Error('Failed to load erasure queue');
  const data = await res.json();
  return data.queue || [];
}

export async function cancelErasure(token: string, businessId: number) {
  const res = await admin(token, `/gdpr/erasure/${businessId}/cancel`, { method: 'PATCH' });
  if (!res.ok) throw new Error('Failed to cancel erasure');
}

export async function rescheduleErasure(token: string, businessId: number, deletion_scheduled_at: string) {
  const res = await admin(token, `/gdpr/erasure/${businessId}/reschedule`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deletion_scheduled_at }),
  });
  if (!res.ok) throw new Error('Failed to reschedule');
}

export async function scheduleErasure(token: string, businessId: number, reason: string, days?: number) {
  const res = await admin(token, `/gdpr/erasure/${businessId}/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, days }),
  });
  if (!res.ok) throw new Error('Failed to schedule erasure');
}

export async function fetchGdprExports(token: string) {
  const res = await admin(token, '/gdpr/exports');
  if (!res.ok) throw new Error('Failed to load exports');
  const data = await res.json();
  return data.exports || [];
}

export async function createGdprExport(
  token: string,
  payload: {
    business_id: number;
    scope: 'business' | 'tenant';
    tenant_id?: number;
    date_from?: string;
    date_to?: string;
    purpose: string;
  }
) {
  const res = await admin(token, '/gdpr/exports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Export failed');
  }
  const data = await res.json();
  return data.export;
}

export async function downloadGdprExport(token: string, exportId: string, filename: string) {
  const res = await admin(token, `/gdpr/exports/${exportId}/download`);
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchGdprAccessLog(
  token: string,
  opts?: { business_id?: number; source?: string; limit?: number }
) {
  const params = new URLSearchParams();
  if (opts?.business_id) params.set('business_id', String(opts.business_id));
  if (opts?.source) params.set('source', opts.source);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const q = params.toString() ? `?${params}` : '';
  const res = await admin(token, `/gdpr/access-log${q}`);
  if (!res.ok) throw new Error('Failed to load access log');
  const data = await res.json();
  return data.entries || [];
}

export async function fetchGdprPolicy(token: string) {
  const res = await admin(token, '/gdpr/policy');
  if (!res.ok) throw new Error('Failed to load policy');
  return res.json();
}

export async function fetchSystemHealth(token: string) {
  const res = await admin(token, '/system/health');
  if (!res.ok) throw new Error('Failed to load system health');
  return res.json();
}

export async function fetchSystemDiagnostics(token: string) {
  const res = await admin(token, '/system/diagnostics');
  if (!res.ok) throw new Error('Failed to load diagnostics');
  return res.json();
}

export async function fetchEmailFailures(token: string, limit = 50) {
  const res = await admin(token, `/system/email-failures?limit=${limit}`);
  if (!res.ok) throw new Error('Failed to load email failures');
  const data = await res.json();
  return data.failures || [];
}

export async function fetchSystemLogs(token: string, limit = 100) {
  const res = await admin(token, `/system/logs?limit=${limit}`);
  if (!res.ok) throw new Error('Failed to load logs');
  const data = await res.json();
  return data.logs || [];
}

export async function fetchBackupStatus(token: string) {
  const res = await admin(token, '/system/backups');
  if (!res.ok) throw new Error('Failed to load backup status');
  return res.json();
}

export async function fetchAccessCases(token: string, all = false) {
  const res = await admin(token, `/access-cases${all ? '?all=true' : ''}`);
  if (!res.ok) throw new Error('Failed to load access cases');
  const data = await res.json();
  return { cases: data.cases || [], ttlHours: data.ttlHours || 4 };
}

export async function checkAccessCase(token: string, businessId: number) {
  const res = await admin(token, `/access-cases/check?business_id=${businessId}`);
  if (!res.ok) throw new Error('Check failed');
  return res.json();
}

export async function openAccessCase(
  token: string,
  payload: { business_id: number; reason: string; purpose?: string }
) {
  const res = await admin(token, '/access-cases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to open access case');
  const data = await res.json();
  return data.case;
}

export async function closeAccessCase(token: string, caseId: string) {
  const res = await admin(token, `/access-cases/${caseId}/close`, { method: 'PATCH' });
  if (!res.ok) throw new Error('Failed to close case');
}

export async function fetchAdminSecurityStatus(token: string) {
  const res = await admin(token, '/security/status');
  if (!res.ok) throw new Error('Failed to load security status');
  return res.json();
}

export async function setupAdmin2fa(token: string) {
  const res = await admin(token, '/security/2fa/setup', { method: 'POST' });
  if (!res.ok) throw new Error('Failed to start 2FA setup');
  return res.json() as Promise<{ secret: string; qr_data_url: string }>;
}

export async function enableAdmin2fa(token: string, code: string) {
  const res = await admin(token, '/security/2fa/enable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to enable 2FA');
  }
  return res.json();
}

export async function disableAdmin2fa(token: string, code: string) {
  const res = await admin(token, '/security/2fa/disable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to disable 2FA');
  }
  return res.json();
}
